<script>
import TicketBoardView from './components/TicketBoardView.vue';
import './tickets-index-styles.css';

const DEFAULT_FILTERS = Object.freeze({
    query: '',
    channel: 'all',
    category: 'all',
    priority: 'all',
    assignee: 'all',
    // Always include closed_group so the Chiusi column is populated by default.
    statuses: ['new', 'assigned', 'in_progress', 'blocked', 'closed_group'],
});

export default {
    name: 'TicketIndexBoard',
    components: { TicketBoardView },
    props: {
        endpoint: { type: String, required: true },
        csrfToken: { type: String, required: true },
        currentUserId: { type: [Number, String], required: true },
        filterOptions: { type: Object, default: () => ({}) },
        channelOperators: { type: Object, default: () => ({}) },
        /** Role-based assign pool: { options: [{value,label}], channels: { channelId: [userId] } }. */
        assigneePool: { type: Object, default: () => ({ options: [], channels: {} }) },
        /** Server-rendered per-channel counters, shared with the classic grid. */
        statusCounts: { type: Array, default: () => [] },
        /** Active desk theme payload (`key` / `label` / `logo_src`) or null. */
        theme: { type: Object, default: null },
        initialCustomerEmail: { type: String, default: '' },
        title: { type: String, default: 'Coda ticket' },
        subtitle: { type: String, default: '' },
        createTicketUrl: { type: String, default: '' },
        createTicketLabel: { type: String, default: 'Add' },
    },
    data() {
        return {
            tickets: [],
            meta: {},
            filters: {
                ...DEFAULT_FILTERS,
                query: this.initialCustomerEmail,
            },
            showArchived: false,
            columnPages: {
                new: 1,
                assigned: 1,
                in_progress: 1,
                blocked: 1,
                closed: 1,
            },
            highlightedId: null,
            error: '',
            abortController: null,
            highlightTimer: null,
            echoChannel: null,
        };
    },
    computed: {
        normalizedOptions() {
            return {
                channels: this.normalizeOptions(this.filterOptions.channel_name),
                categories: this.normalizeOptions(this.filterOptions.category_name),
                priorities: this.normalizeOptions(this.filterOptions.priority),
                assignees: this.normalizeOptions(this.filterOptions.assigned_user_name),
            };
        },
        filteredAssignees() {
            const all = this.normalizedOptions.assignees;
            if (this.filters.channel === 'all') {
                return all;
            }
            const allowed = (this.channelOperators[String(this.filters.channel)] || []).map(String);
            return all.filter((option) => allowed.includes(String(option.value)));
        },
        /**
         * Assign dropdown pool — role holders on the channel, not the grid filter
         * list (which only knows users already assigned to a ticket).
         */
        assignees() {
            const pool = this.normalizeOptions(this.assigneePool.options);
            const scoped = this.filters.channel === 'all'
                ? pool
                : pool.filter((option) => (this.assigneePool.channels[String(this.filters.channel)] || [])
                    .map(String)
                    .includes(String(option.value)));

            return scoped.map((option) => ({
                id: option.value,
                name: option.label,
            }));
        },
        statusOptions() {
            return [
                { value: 'new', label: 'Nuovo' },
                { value: 'assigned', label: 'Assegnato' },
                { value: 'in_progress', label: 'In lavorazione' },
                { value: 'blocked', label: 'Stand-by' },
                { value: 'closed_group', label: 'Chiusi (inclusi uniti)' },
            ];
        },
        boardSubtitle() {
            return this.subtitle
                || this.meta.subtitle
                || '';
        },
        canAssign() {
            return this.meta.can_assign_globally === true;
        },
        paginationByColumn() {
            return this.meta.columns || {};
        },
        archivedCount() {
            return Number(this.meta.archived_total || 0);
        },
    },
    watch: {
        filters: {
            deep: true,
            handler() {
                this.resetColumnPages();
                this.fetchTickets();
            },
        },
        showArchived() {
            this.columnPages.closed = 1;
            this.fetchTickets();
        },
    },
    created() {
        this.fetchTickets();
        this.subscribeToBoardUpdates();
    },
    beforeUnmount() {
        if (this.abortController) {
            this.abortController.abort();
        }
        if (this.highlightTimer) {
            window.clearTimeout(this.highlightTimer);
        }
        this.unsubscribeFromBoardUpdates();
    },
    methods: {
        normalizeOptions(options) {
            if (!Array.isArray(options)) {
                return [];
            }
            return options.map((option) => ({
                value: String(option.value ?? option.id ?? ''),
                label: option.label ?? option.name ?? String(option.value ?? option.id ?? ''),
            }));
        },
        requestHeaders() {
            return {
                Accept: 'application/json',
                'X-Requested-With': 'XMLHttpRequest',
                'X-CSRF-TOKEN': this.csrfToken,
            };
        },
        buildQuery() {
            const query = new URLSearchParams();
            this.filters.statuses.forEach((status) => {
                if (status === 'closed_group') {
                    query.append('status[]', 'closed');
                    query.append('status[]', 'merged');
                    return;
                }
                query.append('status[]', status);
            });
            if (this.filters.channel !== 'all') query.set('ticket_channel_id', this.filters.channel);
            if (this.filters.category !== 'all') query.set('ticket_category_id', this.filters.category);
            if (this.filters.priority !== 'all') query.set('priority', this.filters.priority);
            if (this.filters.assignee !== 'all') query.set('assigned_user_id', this.filters.assignee);
            if (this.filters.query) query.set('customer_email', this.filters.query);
            if (this.showArchived) query.set('include_archived', '1');
            query.set('new_page', String(this.columnPages.new));
            query.set('assigned_page', String(this.columnPages.assigned));
            query.set('in_progress_page', String(this.columnPages.in_progress));
            query.set('blocked_page', String(this.columnPages.blocked));
            query.set('closed_page', String(this.columnPages.closed));
            query.set('per_page', '20');
            return query;
        },
        async fetchTickets() {
            if (this.abortController) {
                this.abortController.abort();
            }
            this.abortController = new AbortController();
            this.error = '';
            try {
                const response = await fetch(`${this.endpoint}?${this.buildQuery().toString()}`, {
                    credentials: 'same-origin',
                    headers: this.requestHeaders(),
                    signal: this.abortController.signal,
                });
                const payload = await response.json().catch(() => ({}));
                if (!response.ok) {
                    // Search / validation (422) must not flash a red banner over the board.
                    if (response.status === 422 || (this.filters.query || '').trim()) {
                        return;
                    }
                    throw new Error(payload.message || 'Impossibile caricare i ticket.');
                }
                const columns = payload.data || {};
                this.tickets = Object.keys(this.columnPages).flatMap(
                    (columnKey) => (Array.isArray(columns[columnKey]) ? columns[columnKey] : []),
                );
                this.meta = payload.meta || {};
            } catch (error) {
                if (error.name !== 'AbortError') {
                    this.error = error.message || 'Impossibile caricare i ticket.';
                }
            }
        },
        /**
         * Reload board pages so a column that lost a card refills from the queue
         * (e.g. assign New → In progress pulls the next New ticket onto page 1).
         */
        async refreshBoardAfterCardMove(ticketId = null) {
            await this.fetchTickets();
            if (ticketId != null) {
                this.highlight(ticketId);
            }
        },
        /**
         * Chiusi is opt-in (TM-UX-012). When a live close/merge lands, enable the
         * closed_group filter so the Chiusi column appears and the card is fetchable.
         * @returns {boolean} true when filters changed (watch will refetch).
         */
        ensureClosedStatusFilter() {
            const statuses = Array.isArray(this.filters.statuses) ? this.filters.statuses : [];
            if (statuses.includes('closed_group')) {
                return false;
            }
            this.filters = {
                ...this.filters,
                statuses: [...statuses, 'closed_group'],
            };
            return true;
        },
        onFilters(next) {
            const normalized = { ...next };
            if (normalized.channel !== this.filters.channel) {
                normalized.assignee = 'all';
            }
            this.filters = normalized;
        },
        resetColumnPages() {
            this.columnPages = {
                new: 1,
                assigned: 1,
                in_progress: 1,
                blocked: 1,
                closed: 1,
            };
        },
        onPage({ columnKey, page }) {
            if (!Object.prototype.hasOwnProperty.call(this.columnPages, columnKey)) {
                return;
            }
            this.columnPages[columnKey] = page;
            this.fetchTickets();
        },
        async mutate(url, body = new URLSearchParams()) {
            const response = await fetch(url, {
                method: 'POST',
                credentials: 'same-origin',
                headers: this.requestHeaders(),
                body,
            });
            const payload = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(payload.message || 'Operazione non riuscita.');
            }
            return payload;
        },
        async onAssign({ ticketId, userId }) {
            const ticket = this.tickets.find((item) => String(item.id) === String(ticketId));
            if (!ticket?.routes?.assign) return;
            try {
                const payload = await this.mutate(
                    ticket.routes.assign,
                    new URLSearchParams({ assigned_user_id: String(userId) }),
                );
                await this.refreshBoardAfterCardMove(ticketId);
                this.toast('success', payload.message || 'Ticket assegnato.');
            } catch (error) {
                this.toast('error', error.message);
            }
        },
        onArchive(ticketId) {
            const ticket = this.tickets.find((item) => String(item.id) === String(ticketId));
            if (ticket?.routes?.archive) {
                this.persistArchive(ticket, false);
            }
        },
        onRestore(ticketId) {
            const ticket = this.tickets.find((item) => String(item.id) === String(ticketId));
            if (ticket?.routes?.restore) {
                this.persistArchive(ticket, true);
            }
        },
        async persistArchive(ticket, restore) {
            try {
                const payload = await this.mutate(restore ? ticket.routes.restore : ticket.routes.archive);
                await this.refreshBoardAfterCardMove(ticket.id);
                this.toast('success', payload.message || (restore ? 'Ticket ripristinato.' : 'Ticket archiviato.'));
            } catch (error) {
                this.toast('error', error.message);
            }
        },
        async onArchiveAll(ids) {
            for (const id of ids) {
                const ticket = this.tickets.find((item) => String(item.id) === String(id));
                if (ticket?.routes?.archive) {
                    await this.persistArchive(ticket, false);
                }
            }
        },
        patchTicket(ticketId, patch) {
            this.tickets = this.tickets.map((ticket) => (
                String(ticket.id) === String(ticketId)
                    ? { ...ticket, ...patch }
                    : ticket
            ));
        },
        highlight(ticketId) {
            this.highlightedId = ticketId;
            if (this.highlightTimer) window.clearTimeout(this.highlightTimer);
            this.highlightTimer = window.setTimeout(() => {
                this.highlightedId = null;
            }, 2600);
        },
        openTicket(ticket) {
            if (ticket?.routes?.show) {
                window.location.href = ticket.routes.show;
            }
        },
        showClassicView() {
            const modern = document.getElementById('ticket-modern-view');
            const classic = document.getElementById('ticket-classic-view');
            if (modern) modern.hidden = true;
            if (classic) classic.hidden = false;
            document.dispatchEvent(new CustomEvent('ticket-classic-view:shown'));
        },
        subscribeToBoardUpdates() {
            if (!window.Echo || !this.currentUserId) return;
            this.echoChannel = `App.User.${this.currentUserId}`;
            window.Echo.private(this.echoChannel)
                .listen('.ticket.board-card.updated', (event) => {
                    const status = String(event?.status || '').toLowerCase();
                    if (status === 'closed' || status === 'merged') {
                        // Enabling closed_group triggers the filters watch → fetch.
                        if (this.ensureClosedStatusFilter()) {
                            this.highlight(event.ticket_id);
                            return;
                        }
                    }
                    // Always reload pages so the column that lost the card refills.
                    this.refreshBoardAfterCardMove(event.ticket_id);
                });
        },
        unsubscribeFromBoardUpdates() {
            if (window.Echo && this.echoChannel) {
                // stopListening only — App.User.* is shared with the topbar bell.
                window.Echo.private(this.echoChannel)
                    .stopListening('.ticket.board-card.updated');
            }
        },
        toast(icon, title) {
            if (window.SwalToast) {
                window.SwalToast.fire({ icon, title });
                return;
            }
            if (icon === 'error' && window.Swal) {
                window.Swal.fire({ icon, title });
            }
        },
    },
};
</script>

<template>
    <div class="ticket-index-board-host">
        <div v-if="error" class="alert alert-danger">{{ error }}</div>
        <TicketBoardView
            :tickets="tickets"
            :filters="filters"
            :channel-options="normalizedOptions.channels"
            :category-options="normalizedOptions.categories"
            :priority-options="normalizedOptions.priorities"
            :assignee-options="filteredAssignees"
            :status-options="statusOptions"
            :assignees="assignees"
            :show-assignment="canAssign"
            :highlighted-id="highlightedId"
            :show-archived="showArchived"
            :archived-count="archivedCount"
            :pagination-by-column="paginationByColumn"
            :status-counts="statusCounts"
            :theme="theme"
            :title="title"
            :subtitle="boardSubtitle"
            :current-user-id="currentUserId"
            :show-simulate="false"
            :show-classic-toggle="true"
            @update:filters="onFilters"
            @update:show-archived="showArchived = $event"
            @assign="onAssign"
            @archive="onArchive"
            @archive-all="onArchiveAll"
            @restore="onRestore"
            @page="onPage"
            @open="openTicket"
            @classic-view="showClassicView"
        >
            <template v-if="createTicketUrl" #filter-actions>
                <a
                    :href="createTicketUrl"
                    class="tickets-index-btn tickets-index-btn--primary"
                >{{ createTicketLabel }}</a>
            </template>
        </TicketBoardView>
    </div>
</template>
